import { useState, useEffect, useLayoutEffect, useContext, createContext, ReactNode, Dispatch, SetStateAction, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    langFromPathname,
    switchLocalePathname,
    getEffectiveLocationHash,
} from "./navigation/navPathUtils";
import { HealthLang } from "./health/healthTypes";

function withSearchAndHash(path: string, search: string, hash: string): string {
    return `${path}${search}${hash}`;
}

function toNavigateTarget(
    pathname: string,
    search: string,
    hash: string
): { pathname: string; search: string; hash: string } {
    return {
        pathname,
        search,
        hash,
    };
}

// create parent provider
export default function AppProviders({ children } : { children: ReactNode }) {
    return (
        <MobileNavOpenProvider>
            <NavTabProvider>
                <LangProvider>
                    <ScreenSizeProvider>
                        {children}
                    </ScreenSizeProvider>
                </LangProvider>
            </NavTabProvider>
        </MobileNavOpenProvider>
    );
};


///////////////////
// SCREEN WIDTH //
//////////////////
type ScreenSize = "Small" | "Large";

interface ScreenSizeContextProps {
    screenSize: ScreenSize;
    setScreenSize: Dispatch<SetStateAction<ScreenSize>>;
};
const ScreenSizeContext = createContext<ScreenSizeContextProps | null>(null);

interface ScreenSizeProviderProps {
    children: ReactNode;
}
export function ScreenSizeProvider({ children } : ScreenSizeProviderProps) {
  const [screenSize, setScreenSize] = useState<ScreenSize>("Large");

    useEffect(() => {
        handleResize();
        function handleResize() {
            const width = window.innerWidth;
            if (width <= 1024) {
                setScreenSize("Small")
            } else if (width > 1024) {
                setScreenSize("Large")
            };
        };

        window.addEventListener('resize', handleResize);
        // Cleanup event listener on component unmount
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return (
        <ScreenSizeContext.Provider value={{ screenSize, setScreenSize }}>
            {children}
        </ScreenSizeContext.Provider>
    );
};

export function useScreenSize() {
    const context = useContext(ScreenSizeContext);
    if (!context) {
        throw new Error('useScreenSize must be used within a ScreenSizeProvider');
    };
    return context
};


///////////////
// LANGUAGE //
//////////////
interface LangContextProps {
    lang: string;
    setLang: (targetLang: string) => void;
};
export const LangContext = createContext<LangContextProps | null>(null);

interface LangProviderProps {
    children: ReactNode;
};
export function LangProvider({ children } : LangProviderProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const userPickedLangRef = useRef(false);
    const [preferredLang, setPreferredLang] = useState<HealthLang>(() =>
        langFromPathname(location.pathname)
    );
    const [lang, setLangState] = useState<string>(() =>
        langFromPathname(location.pathname)
    );

    useLayoutEffect(() => {
        const urlLang = langFromPathname(location.pathname);
        const activeHash = getEffectiveLocationHash(location.hash);

        if (userPickedLangRef.current) {
            const localizedPath = switchLocalePathname(
                location.pathname,
                preferredLang
            );
            const localizedFullPath = withSearchAndHash(
                localizedPath,
                location.search,
                activeHash
            );
            const currentFullPath = withSearchAndHash(
                location.pathname,
                location.search,
                activeHash
            );

            if (currentFullPath !== localizedFullPath) {
                navigate(
                    toNavigateTarget(
                        localizedPath,
                        location.search,
                        activeHash
                    ),
                    { replace: true }
                );
                return;
            }

            setLangState(preferredLang);
            return;
        }

        setPreferredLang(urlLang);
        setLangState(urlLang);
    }, [
        location.pathname,
        location.search,
        location.hash,
        preferredLang,
        navigate,
    ]);

    const setLang = (targetLang: string) => {
        const nextLang = targetLang as HealthLang;
        const activeHash = getEffectiveLocationHash(location.hash);
        userPickedLangRef.current = true;
        setPreferredLang(nextLang);

        const nextPath = switchLocalePathname(location.pathname, nextLang);
        const nextFullPath = withSearchAndHash(
            nextPath,
            location.search,
            activeHash
        );
        const currentFullPath = withSearchAndHash(
            location.pathname,
            location.search,
            activeHash
        );

        if (nextFullPath !== currentFullPath) {
            navigate(
                toNavigateTarget(nextPath, location.search, activeHash),
                { replace: true }
            );
        }
        setLangState(nextLang);
    };
    
    return (
        <LangContext.Provider value={{ lang, setLang }}>
            { children }
        </LangContext.Provider>
    );
};

export function useLang() {
    const info = useContext(LangContext);
    if (!info) {
        throw new Error('useLang must be used within a StateContext');
    };
    return info
};

/////////////
// NAV TAB //
/////////////
interface NavTabContextProps {
    navTab: {"Name": string, "Open": boolean, "LeftPos": number};
    setNavTab: Dispatch<SetStateAction<{"Name": string, "Open": boolean, "LeftPos": number}>>;
};
export const NavTabContext = createContext<NavTabContextProps | null>(null);


interface NavTabProviderProps {
    children: ReactNode;
};
export function NavTabProvider({ children } : NavTabProviderProps) {
    const [navTab, setNavTab] = useState<{"Name": string, "Open": boolean, "LeftPos": number}>({"Name": "", "Open": false, "LeftPos": 0});
    
    return (
        <NavTabContext.Provider value={{ navTab, setNavTab }}>
            { children }
        </NavTabContext.Provider>
    );
};

export function useNavTab() {
    const info = useContext(NavTabContext);
    if (!info) {
        throw new Error('useNavTab must be used within a StateContext');
    }
    return info
};

/////////////////
// MOBILE NAV //
////////////////
interface MobileNavOpenContextProps {
    mobileNavOpen: boolean;
    setMobileNavOpen: Dispatch<SetStateAction<boolean>>;
};
export const MobileNavOpenContext = createContext<MobileNavOpenContextProps | null>(null);

interface MobileNavOpenProviderProps {
    children: ReactNode;
};
export function MobileNavOpenProvider({ children } : MobileNavOpenProviderProps) {
    const [mobileNavOpen, setMobileNavOpen] = useState<boolean>(false);
    
    return (
        <MobileNavOpenContext.Provider value={{ mobileNavOpen, setMobileNavOpen }}>
            { children }
        </MobileNavOpenContext.Provider>
    );
};

export function useMobileNavOpen() {
    const info = useContext(MobileNavOpenContext);
    if (!info) {
        throw new Error('useMobileNavOpen must be used within a MobileNavOpenContext');
    }
    return info
};